import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { LogIn, UserPlus, Sparkles, Send } from "lucide-react";

export function DemoSection() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/demo", form);
      toast.success(data.message || t("demo.sent"));
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("demo.sendError"));
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section id="demo" className="relative py-24 lg:py-32" data-testid="demo-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          {/* Connexion */}
          <div>
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#dc143c]">
              {t("demo.eyebrow")}
            </span>
            <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
              {t("demo.titleA")} <span className="text-[#EAB308]">{t("demo.titleB")}</span>
            </h2>
            <p className="mt-4 text-white/60 text-lg">
              {t("demo.desc")}
            </p>

            <div className="mt-8 space-y-3">
              <button
                data-testid="connect-login-btn"
                onClick={() => navigate("/login")}
                className="group flex w-full items-center gap-4 rounded-lg border border-white/10 bg-[#0a0a0a] p-5 text-left transition-colors hover:border-[#EAB308]/50"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#EAB308]/12 border border-[#EAB308]/30">
                  <LogIn className="w-5 h-5 text-[#EAB308]" />
                </div>
                <div>
                  <div className="font-semibold text-white">{t("demo.loginT")}</div>
                  <div className="text-sm text-white/50">{t("demo.loginS")}</div>
                </div>
              </button>

              <button
                data-testid="connect-register-btn"
                onClick={() => navigate("/register")}
                className="group flex w-full items-center gap-4 rounded-lg border border-white/10 bg-[#0a0a0a] p-5 text-left transition-colors hover:border-[#dc143c]/50"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#dc143c]/12 border border-[#dc143c]/30">
                  <UserPlus className="w-5 h-5 text-[#dc143c]" />
                </div>
                <div>
                  <div className="font-semibold text-white">{t("demo.registerT")}</div>
                  <div className="text-sm text-white/50">{t("demo.registerS")}</div>
                </div>
              </button>

              <button
                data-testid="connect-demo-btn"
                onClick={() => navigate("/login")}
                className="group flex w-full items-center gap-4 rounded-lg border border-white/10 bg-[#0a0a0a] p-5 text-left transition-colors hover:border-white/25"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-white/8 border border-white/15">
                  <Sparkles className="w-5 h-5 text-white/80" />
                </div>
                <div>
                  <div className="font-semibold text-white">{t("demo.demoT")}</div>
                  <div className="text-sm text-white/50">{t("demo.demoS")}</div>
                </div>
              </button>
            </div>
          </div>

          {/* Demo form */}
          <div className="rounded-xl border border-white/10 bg-[#0a0a0a] p-8">
            <h3 className="text-2xl font-bold text-white">{t("demo.formTitle")}</h3>
            <p className="mt-1 text-sm text-white/50">
              {t("demo.formSub")}
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <Input
                data-testid="demo-name"
                required placeholder={t("demo.name")} value={form.name} onChange={set("name")}
                className="bg-black/40 border-white/15 text-white placeholder:text-white/30 h-11"
              />
              <Input
                data-testid="demo-email"
                required type="email" placeholder={t("demo.email")} value={form.email} onChange={set("email")}
                className="bg-black/40 border-white/15 text-white placeholder:text-white/30 h-11"
              />
              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  data-testid="demo-company"
                  placeholder={t("demo.company")} value={form.company} onChange={set("company")}
                  className="bg-black/40 border-white/15 text-white placeholder:text-white/30 h-11"
                />
                <Input
                  data-testid="demo-phone"
                  placeholder={t("demo.phone")} value={form.phone} onChange={set("phone")}
                  className="bg-black/40 border-white/15 text-white placeholder:text-white/30 h-11"
                />
              </div>
              <Textarea
                data-testid="demo-message"
                placeholder={t("demo.message")} value={form.message} onChange={set("message")}
                className="bg-black/40 border-white/15 text-white placeholder:text-white/30 min-h-[96px]"
              />
              <Button
                data-testid="demo-submit"
                type="submit" disabled={loading}
                className="w-full h-11 bg-[#EAB308] text-black font-semibold rounded-md gap-2 hover:opacity-90 transition-opacity"
              >
                <Send className="w-4 h-4" />
                {loading ? t("demo.sending") : t("demo.send")}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
